import React, { useState } from 'react';
import { Container, Typography, Box, Grid, TextField, InputAdornment, MenuItem, Select, 
    FormControl, InputLabel,Chip,Paper,Button, useTheme, CssBaseline
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import QuestionCard from '../../components/question_bank/QuestionCard';
import { interviewQuestions } from '../../components/question_bank/questionBankData';
import { TopBar } from '../../components/top_bar/TopBar';
import { Helmet } from 'react-helmet';

const QuestionBank = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterJobTitle, setFilterJobTitle] = useState('');
  const theme = useTheme();
  const TITLE = 'Question Bank - Mirwork';

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCompanyFilterChange = (event) => {
    setFilterCompany(event.target.value);
  };

  const handleJobTitleFilterChange = (event) => {
    setFilterJobTitle(event.target.value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterCompany('');
    setFilterJobTitle('');
  };

  const companies = [...new Set(interviewQuestions.map(item => item.company))];
  const jobTitles = [...new Set(interviewQuestions.map(item => item.jobTitle))];

  const filteredQuestions = interviewQuestions.filter(item => {
    const matchesSearch = 
      searchTerm === '' || 
      item.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.jobDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.questions.some(q => q.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCompany = filterCompany === '' || item.company === filterCompany;
    const matchesJobTitle = filterJobTitle === '' || item.jobTitle === filterJobTitle;
    
    return matchesSearch && matchesCompany && matchesJobTitle;
  });

  return (
    <>
    <Helmet>
        <title>{TITLE}</title>
    </Helmet>
    <TopBar />
    <CssBaseline />
    <Container maxWidth="lg" sx={{bgcolor: "#f5f5f5"}}>
      <Box sx={{ py: 4}}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI Interview Question Bank
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Prepare for your next interview with our extensive collection of interview questions from top companies
          </Typography>
        </Box>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            border: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search for questions, companies, or job titles..."
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="company-filter-label">Company</InputLabel>
                <Select
                  labelId="company-filter-label"
                  value={filterCompany}
                  onChange={handleCompanyFilterChange}
                  label="Company"
                >
                  <MenuItem value="">All Companies</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company} value={company}>{company}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="job-filter-label">Job Title</InputLabel>
                <Select
                  labelId="job-filter-label"
                  value={filterJobTitle}
                  onChange={handleJobTitleFilterChange}
                  label="Job Title"
                >
                  <MenuItem value="">All Job Titles</MenuItem>
                  {jobTitles.map((title) => (
                    <MenuItem key={title} value={title}>{title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
              <Typography variant="body2" color="text.secondary">
                Filters:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, ml: 1, flexWrap: 'wrap' }}>
                {filterCompany && (
                  <Chip 
                    label={`Company: ${filterCompany}`} 
                    onDelete={() => setFilterCompany('')}
                    size="small"
                    sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
                  />
                )}
                {filterJobTitle && (
                  <Chip 
                    label={`Job: ${filterJobTitle}`} 
                    onDelete={() => setFilterJobTitle('')}
                    size="small"
                    sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.1) }}
                  />
                )}
                {searchTerm && (
                  <Chip 
                    label={`Search: ${searchTerm}`} 
                    onDelete={() => setSearchTerm('')}
                    size="small"
                    sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}
                  />
                )}
                {(filterCompany || filterJobTitle || searchTerm) && (
                  <Button size="small" onClick={resetFilters} sx={{ ml: 1 }}>
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>
            
            <Button 
              variant="contained"
              startIcon={<AddIcon />}
              className="glow-effect"
              onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLScYhxPJfPFI82eib6q7ta-8dcJLkDTO35xFISIPZAfRu3h1QQ/viewform?usp=header', '_blank')}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Add Question
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">
            Showing {filteredQuestions.length} results
          </Typography>
        </Box>

        {filteredQuestions.length > 0 ? (
          filteredQuestions.map((item, index) => (
            <QuestionCard
              key={index}
              company={item.company}
              jobTitle={item.jobTitle}
              jobDescription={item.jobDescription}
              questions={item.questions}
            />
          ))
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No questions found matching your criteria.
            </Typography>
            <Button 
              variant="outlined" 
              onClick={resetFilters}
              sx={{ mt: 2 }}
            >
              Reset Filters
            </Button>
          </Paper>
        )}
      </Box>
    </Container>
    </>
  );
};

export default QuestionBank;